import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import api, { IMAGE_BASE_URL } from "../api/Api.js";
import { useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";

import { useLanguage } from "../context/LanguageContext";

// ============================================================
// LOCATIONS
// ============================================================

const LOCATIONS = {
  30: "Airport Heraklion",
  29: "Airport Chania",
  28: "Airport Sitia",
  27: "Harbour Heraklion",
  26: "Harbour Chania",
  25: "Harbour Sitia",
  24: "Accommodation (apartment, villa, hotel etc.)",
};

// ============================================================
// COMPONENT
// ============================================================

function BookACarThree() {
  const navigate = useNavigate();

  const {
    language,
    t,
  } = useLanguage();

  // ==========================================================
  // LANGUAGE
  // ==========================================================

  const languageId = Number(
    language?.lang_id || 1
  );

  // ==========================================================
  // TRANSLATION HELPERS
  // ==========================================================

  const bt = (key, fallback = "") => {
    return t(
      `bookingengine.${key}`,
      fallback
    );
  };

  const pt = (key, fallback = "") => {
    return t(
      `personaldtls.${key}`,
      fallback
    );
  };

  const rt = (key, fallback = "") => {
    return t(
      `ratechart.${key}`,
      fallback
    );
  };

  const et = (key, fallback = "") => {
    return t(
      `extracharges.${key}`,
      fallback
    );
  };

  // ==========================================================
  // STATE
  // ==========================================================

  const [car, setCar] = useState(null);

  const [bookingCriteria, setBookingCriteria] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  // ==========================================================
  // COUPON STATE
  // ==========================================================

  const [couponCode, setCouponCode] =
    useState("");

  const [couponLoading, setCouponLoading] =
    useState(false);

  const [couponMessage, setCouponMessage] =
    useState("");

  const [couponError, setCouponError] =
    useState("");

  // Actual discount amount in €
  const [couponDiscount, setCouponDiscount] =
    useState(0);

  // Percentage returned by coupon
  const [couponDiscountPercentage, setCouponDiscountPercentage] =
    useState(0);

  // Applied coupon object
  const [appliedCoupon, setAppliedCoupon] =
    useState(null);

  // ==========================================================
  // EXTRAS
  // ==========================================================

  const [freeExtras, setFreeExtras] =
    useState([]);

  const [paidExtras, setPaidExtras] =
    useState([]);

  // ==========================================================
  // INSURANCE
  // ==========================================================

  const [freeInsurance, setFreeInsurance] =
    useState([]);

  const [paidInsurance, setPaidInsurance] =
    useState([]);

  // ==========================================================
  // FREE EXTRA QUANTITIES / CHECKBOXES
  // ==========================================================

  const [selectedFreeExtras, setSelectedFreeExtras] =
    useState({});

  const [freeExtraChecked, setFreeExtraChecked] =
    useState({});

  // ==========================================================
  // PAID EXTRA QUANTITIES / CHECKBOXES
  // ==========================================================

  const [selectedPaidExtras, setSelectedPaidExtras] =
    useState({});

  const [paidExtraChecked, setPaidExtraChecked] =
    useState({});

  // ==========================================================
  // INSURANCE CHECKBOXES
  // ==========================================================

  const [freeInsuranceChecked, setFreeInsuranceChecked] =
    useState({});

  const [paidInsuranceChecked, setPaidInsuranceChecked] =
    useState({});

  // ==========================================================
  // CUSTOMER INFORMATION
  // ==========================================================

  const [customer, setCustomer] = useState({
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    phone: "",
    mobile: "",
    address: "",
    city: "",
    country: "",
    postcode: "",
    remarks: "",
    dropoffRemarks: "",
  });

  // ==========================================================
  // PAYMENT / TERMS
  // ==========================================================

  const [paymentMethod, setPaymentMethod] =
    useState("paynow");

  const [acceptedTerms, setAcceptedTerms] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  // ==========================================================
  // TERMS
  // ==========================================================

  const [termsContent, setTermsContent] =
    useState(null);

  const [termsLoading, setTermsLoading] =
    useState(false);

  const [showTermsModal, setShowTermsModal] =
    useState(false);

  // ==========================================================
  // LOAD TERMS
  // ==========================================================

  useEffect(() => {
    const loadTermsAndConditions = async () => {
      try {
        setTermsLoading(true);

        const response = await api.get(
          "/contents/content",
          {
            params: {
              page_id: 20,
              lang_id: languageId,
            },
          }
        );

        const result = response.data;

        if (
          !result ||
          !result.success ||
          !result.data
        ) {
          setTermsContent(null);
          return;
        }

        setTermsContent(result.data);
      } catch (error) {
        console.error(
          "ERROR LOADING TERMS:",
          error
        );

        setTermsContent(null);
      } finally {
        setTermsLoading(false);
      }
    };

    loadTermsAndConditions();
  }, [languageId]);

  // ==========================================================
  // CUSTOMER CHANGE
  // ==========================================================

  const handleCustomerChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setCustomer((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // ==========================================================
  // LOAD BOOKING DATA
  // ==========================================================

  useEffect(() => {
    try {
      const storedCar =
        sessionStorage.getItem(
          "selectedCar"
        );

      const storedCriteria =
        sessionStorage.getItem(
          "bookingCriteria"
        );

      if (!storedCar) {
        console.warn(
          "No selected car found"
        );

        navigate("/bookacar");
        return;
      }

      const parsedCar =
        JSON.parse(storedCar);

      setCar(parsedCar);

      if (storedCriteria) {
        setBookingCriteria(
          JSON.parse(storedCriteria)
        );
      }
    } catch (error) {
      console.error(
        "Error loading booking information:",
        error
      );

      navigate("/bookacar");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ==========================================================
  // LOAD EXTRAS + INSURANCE
  // ==========================================================

  useEffect(() => {
    const loadExtras = async () => {
      try {
        const response = await api.get(
          "/extras",
          {
            params: {
              lang_id: languageId,
            },
          }
        );

        const result = response.data;

        if (
          !result ||
          !result.success
        ) {
          throw new Error(
            result?.message ||
              "Failed to load extras"
          );
        }

        setFreeExtras(
          Array.isArray(result.freeExtras)
            ? result.freeExtras
            : []
        );

        setPaidExtras(
          Array.isArray(result.paidExtras)
            ? result.paidExtras
            : []
        );

        setFreeInsurance(
          Array.isArray(result.freeInsurance)
            ? result.freeInsurance
            : []
        );

        setPaidInsurance(
          Array.isArray(result.paidInsurance)
            ? result.paidInsurance
            : []
        );
      } catch (error) {
        console.error(
          "Error loading extras:",
          error
        );

        setFreeExtras([]);
        setPaidExtras([]);
        setFreeInsurance([]);
        setPaidInsurance([]);
      }
    };

    loadExtras();
  }, [languageId]);

  // ==========================================================
  // INITIALIZE FREE EXTRAS
  // ==========================================================

  useEffect(() => {
    const quantities = {};
    const checked = {};

    freeExtras.forEach((item) => {
      const id = Number(
        item.optdet_id ??
          item.optitem_id
      );

      if (!id) {
        return;
      }

      quantities[id] = 1;
      checked[id] = true;
    });

    setSelectedFreeExtras(
      quantities
    );

    setFreeExtraChecked(
      checked
    );
  }, [freeExtras]);

  // ==========================================================
  // INITIALIZE PAID EXTRAS
  // ==========================================================

  useEffect(() => {
    const quantities = {};
    const checked = {};

    paidExtras.forEach((item) => {
      const id = Number(
        item.optdet_id ??
          item.optitem_id
      );

      if (!id) {
        return;
      }

      quantities[id] = 1;
      checked[id] = true;
    });

    setSelectedPaidExtras(
      quantities
    );

    setPaidExtraChecked(
      checked
    );
  }, [paidExtras]);

  // ==========================================================
  // INITIALIZE FREE INSURANCE
  // ==========================================================

  useEffect(() => {
    const checked = {};

    freeInsurance.forEach(
      (item) => {
        const id = Number(
          item.insitem_id
        );

        if (!id) {
          return;
        }

        checked[id] = true;
      }
    );

    setFreeInsuranceChecked(
      checked
    );
  }, [freeInsurance]);

  // ==========================================================
  // INITIALIZE PAID INSURANCE
  // ==========================================================

  useEffect(() => {
    const checked = {};

    paidInsurance.forEach(
      (item) => {
        const id = Number(
          item.insdtl_id ??
            item.insitem_id
        );

        if (!id) {
          return;
        }

        checked[id] = true;
      }
    );

    setPaidInsuranceChecked(
      checked
    );
  }, [paidInsurance]);

  // ==========================================================
  // CAR IMAGE
  // ==========================================================

  const getCarImage = (image) => {
    if (!image) {
      return "";
    }

    let imagePath =
      String(image).trim();

    if (!imagePath) {
      return "";
    }

    if (
      imagePath.startsWith(
        "http://"
      ) ||
      imagePath.startsWith(
        "https://"
      )
    ) {
      return imagePath;
    }

    imagePath =
      imagePath.replace(
        /^\/+/,
        ""
      );

    const imageBase =
      IMAGE_BASE_URL ||
      "http://localhost:5555/uploads";

    if (
      imagePath.startsWith(
        "images/"
      )
    ) {
      return `${imageBase}/${imagePath}`;
    }

    if (
      imagePath.startsWith(
        "CarImage/"
      )
    ) {
      return `${imageBase}/images/${imagePath}`;
    }

    return `${imageBase}/images/CarImage/${imagePath}`;
  };

  // ==========================================================
  // DATE / TIME / LOCATION
  // ==========================================================

  const formatDate = (date) => {
    if (!date) {
      return "";
    }

    return String(date);
  };

  const getTime = (
    hour,
    minute
  ) => {
    if (
      hour === undefined ||
      hour === null ||
      hour === ""
    ) {
      return "";
    }

    return `${hour}:${String(
      minute ?? "00"
    ).padStart(2, "0")}`;
  };

  const getLocationName = (id) => {
    if (
      id === undefined ||
      id === null ||
      id === ""
    ) {
      return bt(
        "notSelected",
        "Not selected"
      );
    }

    return (
      LOCATIONS[String(id)] ||
      id
    );
  };

  // ==========================================================
  // RENTAL DAYS
  // ==========================================================

  const rentalDays = useMemo(() => {
    if (!bookingCriteria) {
      return 1;
    }

    const pickup =
      bookingCriteria.pickdate ||
      bookingCriteria.pickDate;

    const drop =
      bookingCriteria.returndate ||
      bookingCriteria.returnDate;

    if (!pickup || !drop) {
      return 1;
    }

    const parseDate = (value) => {
      if (value instanceof Date) {
        return value;
      }

      const stringValue =
        String(value);

      const parts =
        stringValue.split("/");

      if (parts.length === 3) {
        const day =
          Number(parts[0]);

        const month =
          Number(parts[1]) - 1;

        const year =
          Number(parts[2]);

        return new Date(
          year,
          month,
          day
        );
      }

      return new Date(
        stringValue
      );
    };

    const pickupDate =
      parseDate(pickup);

    const returnDate =
      parseDate(drop);

    if (
      Number.isNaN(
        pickupDate.getTime()
      ) ||
      Number.isNaN(
        returnDate.getTime()
      )
    ) {
      return 1;
    }

    const difference =
      returnDate.getTime() -
      pickupDate.getTime();

    const days =
      Math.ceil(
        difference /
          (1000 *
            60 *
            60 *
            24)
      );

    return Math.max(
      1,
      days
    );
  }, [bookingCriteria]);

  // ==========================================================
  // CAR DETAILS
  // ==========================================================

  const carName =
    car?.carmodel_name ||
    car?.name ||
    car?.carmodel ||
    bt(
      "selectedCar",
      "Selected car"
    );

  const category =
    car?.cat_name ||
    car?.category ||
    "";

  const carImage =
    getCarImage(
      car?.carmodel_image ||
        car?.image
    );

  const passengers =
    car?.carmodel_passgrno ??
    car?.passengers ??
    0;

  const luggage =
    car?.carmodel_luggno ??
    car?.luggage ??
    0;

  const transmission =
    car?.transmission ||
    car?.gear ||
    bt(
      "manual",
      "Manual"
    );

  const airCondition =
    car?.airco ||
    car?.air_condition ||
    "Air-Co";

  // ==========================================================
  // PRICE
  // ==========================================================

  const pricePerDay =
    Number(
      car?.pricePerDay ??
        car?.price_perday ??
        car?.price ??
        0
    );

  const storedFinalPrice =
    Number(
      car?.finalPrice ??
        car?.final_price ??
        0
    );

  const rentalPrice =
    storedFinalPrice > 0
      ? storedFinalPrice
      : pricePerDay *
        rentalDays;

  // ==========================================================
  // LOCATION CHARGES
  // ==========================================================

  const pickupCharge =
    Number(
      bookingCriteria?.pickupCharge ??
        bookingCriteria?.pickup_charge ??
        20
    );

  const dropoffCharge =
    Number(
      bookingCriteria?.dropoffCharge ??
        bookingCriteria?.dropoff_charge ??
        15
    );

  const oneWayCharge =
    Number(
      bookingCriteria?.oneWayCharge ??
        bookingCriteria?.one_way_charge ??
        25
    );

  // ==========================================================
  // RENTAL TOTAL BEFORE EXTRAS
  // ==========================================================

  const rentalTotal =
    rentalPrice +
    pickupCharge +
    dropoffCharge +
    oneWayCharge;

  // ==========================================================
  // FREE EXTRA HANDLERS
  // ==========================================================

  const updateFreeExtra = (
    id,
    value
  ) => {
    const quantity =
      Number(value);

    setSelectedFreeExtras(
      (previous) => ({
        ...previous,
        [id]:
          quantity >= 1
            ? quantity
            : 1,
      })
    );
  };

  const toggleFreeExtra = (
    id
  ) => {
    setFreeExtraChecked(
      (previous) => ({
        ...previous,
        [id]:
          !previous[id],
      })
    );
  };

  // ==========================================================
  // PAID EXTRA HANDLERS
  // ==========================================================

  const togglePaidExtra = (
    id
  ) => {
    setPaidExtraChecked(
      (previous) => ({
        ...previous,
        [id]:
          !previous[id],
      })
    );
  };

  const updatePaidExtra = (
    id,
    value
  ) => {
    const quantity =
      Number(value);

    setSelectedPaidExtras(
      (previous) => ({
        ...previous,
        [id]:
          quantity >= 1
            ? quantity
            : 1,
      })
    );
  };

  // ==========================================================
  // INSURANCE HANDLERS
  // ==========================================================

  const toggleFreeInsurance = (
    id
  ) => {
    setFreeInsuranceChecked(
      (previous) => ({
        ...previous,
        [id]:
          !previous[id],
      })
    );
  };

  const togglePaidInsurance = (
    id
  ) => {
    setPaidInsuranceChecked(
      (previous) => ({
        ...previous,
        [id]:
          !previous[id],
      })
    );
  };

  // ==========================================================
  // PAID EXTRA CALCULATION
  // ==========================================================

  const getPaidExtraBaseTotal = (
    item
  ) => {
    const dayRate =
      Number(
        item.dayRate ??
          item.day_rate ??
          item.ratePerDay ??
          item.rate_per_day ??
          0
      );

    let ratePaid =
      dayRate *
      rentalDays;

    const maxRate =
      Number(
        item.maxRate ??
          item.max_rate ??
          0
      );

    if (
      maxRate > 0 &&
      ratePaid > maxRate
    ) {
      ratePaid =
        maxRate;
    }

    return ratePaid;
  };

  // ==========================================================
  // PAID EXTRAS TOTAL
  // ==========================================================

  const paidExtrasTotal =
    paidExtras.reduce(
      (total, item) => {
        const id =
          Number(
            item.optdet_id ??
              item.optitem_id
          );

        const checked =
          paidExtraChecked[id] ===
          true;

        if (!checked) {
          return total;
        }

        const quantity =
          Number(
            selectedPaidExtras[
              id
            ] || 1
          );

        const baseTotal =
          getPaidExtraBaseTotal(
            item
          );

        return (
          total +
          baseTotal *
            quantity
        );
      },
      0
    );

  // ==========================================================
  // PAID INSURANCE TOTAL
  // ==========================================================

  const paidInsuranceTotal =
    paidInsurance.reduce(
      (total, item) => {
        const id =
          Number(
            item.insdtl_id ??
              item.insitem_id
          );

        const checked =
          paidInsuranceChecked[
            id
          ] === true;

        if (!checked) {
          return total;
        }

        const amount =
          Number(
            item.chargePerDay ??
              item.charge_per_day ??
              0
          ) *
          rentalDays;

        return (
          total + amount
        );
      },
      0
    );

  // ==========================================================
  // TOTAL EXTRA CHARGES
  // ==========================================================

  const totalExtraCharges =
    paidExtrasTotal +
    paidInsuranceTotal;

  // ==========================================================
  // TOTAL BEFORE COUPON
  // ==========================================================

  const totalBeforeCoupon =
    rentalTotal +
    totalExtraCharges;

  // ==========================================================
  // COUPON DISCOUNT
  // ==========================================================

  const couponDiscountAmount =
    Math.min(
      Math.max(
        Number(
          couponDiscount || 0
        ),
        0
      ),
      totalBeforeCoupon
    );

  // ==========================================================
  // FINAL TOTAL
  // ==========================================================

  const finalTotal =
    Math.max(
      0,
      totalBeforeCoupon -
        couponDiscountAmount
    );

  // ==========================================================
  // OPEN TERMS
  // ==========================================================

  const openTerms = (e) => {
    if (e) {
      e.preventDefault();
    }

    setShowTermsModal(true);
  };

  // ==========================================================
  // CLOSE TERMS
  // ==========================================================

  const closeTerms = () => {
    setShowTermsModal(false);
  };

  // ==========================================================
  // APPLY COUPON
  // ==========================================================

  const handleApplyCoupon =
    async () => {
      const code =
        couponCode.trim();

      if (!code) {
        setCouponError(
          "Please enter coupon code."
        );

        return;
      }

      const selectedModelId =
        car?.selectedmodelid ||
        car?.carmodel_id ||
        car?.carmodelId ||
        car?.id;

      if (!selectedModelId) {
        setCouponError(
          "Car model not found."
        );

        return;
      }

      if (!bookingCriteria) {
        setCouponError(
          "Booking information not found."
        );

        return;
      }

      const estimatedTotal =
        Number(
          totalBeforeCoupon || 0
        );

      if (estimatedTotal <= 0) {
        setCouponError(
          "Invalid total amount."
        );

        return;
      }

      try {
        setCouponLoading(true);
        setCouponMessage("");
        setCouponError("");

        console.log(
          "================================="
        );

        console.log(
          "VALIDATING COUPON"
        );

        console.log(
          "================================="
        );

        console.log({
          coupon: code,
          selectedmodelid:
            selectedModelId,
          pickdate:
            bookingCriteria.pickdate,
          returndate:
            bookingCriteria.returndate,
          estimatedTotal,
        });

        const response =
          await api.post(
            "/coupons/validate",
            {
              coupon: code,
              selectedmodelid:
                selectedModelId,
              pickdate:
                bookingCriteria.pickdate,
              returndate:
                bookingCriteria.returndate,
              estimatedTotal:
                estimatedTotal,
            }
          );

        console.log(
          "COUPON API RESPONSE:",
          response.data
        );

        const data =
          response.data || {};

        // ====================================================
        // VALID COUPON
        // ====================================================

        if (
          data.isvalid === "1" ||
          data.isvalid === 1 ||
          data.valid === true
        ) {
          const discountPercentage =
            Number(
              data.discount || 0
            );

          /*
           * PHP logic:
           *
           * percentInDecimal = discount / 100
           * percent = percentInDecimal * Estimatedtotal
           *
           * We reproduce the same calculation here.
           */

          const calculatedDiscount =
            (
              estimatedTotal *
              discountPercentage
            ) / 100;

          const discountAmount =
            Math.min(
              Math.max(
                calculatedDiscount,
                0
              ),
              estimatedTotal
            );

          const returnedNewPrice =
            Number(
              data.newprice || 0
            );

          const newPrice =
            returnedNewPrice > 0
              ? returnedNewPrice
              : estimatedTotal -
                discountAmount;

          // ==================================================
          // SAVE COUPON
          // ==================================================

          setCouponDiscount(
            discountAmount
          );

          setCouponDiscountPercentage(
            discountPercentage
          );

          setAppliedCoupon({
            code:
              data.code_name ||
              code,

            code_name:
              data.code_name ||
              code,

            discountType:
              "percentage",

            discountValue:
              discountPercentage,

            discount:
              discountPercentage,

            discountAmount:
              discountAmount,

            newprice:
              newPrice,
          });

          setCouponMessage(
            `Coupon "${data.code_name || code}" applied successfully.`
          );

          setCouponError("");

          console.log(
            "COUPON APPLIED:",
            {
              code:
                data.code_name ||
                code,

              discountPercentage,

              discountAmount,

              estimatedTotal,

              finalAmount:
                estimatedTotal -
                discountAmount,
            }
          );
        }

        // ====================================================
        // INVALID COUPON
        // ====================================================

        else {
          setCouponDiscount(0);

          setCouponDiscountPercentage(
            0
          );

          setAppliedCoupon(
            null
          );

          setCouponMessage("");

          setCouponError(
            data.message ||
              "Invalid Coupon Code!!"
          );
        }
      } catch (error) {
        console.error(
          "COUPON VALIDATION ERROR:",
          error
        );

        console.error(
          "COUPON ERROR RESPONSE:",
          error.response?.data
        );

        setCouponDiscount(0);

        setCouponDiscountPercentage(
          0
        );

        setAppliedCoupon(
          null
        );

        setCouponMessage("");

        setCouponError(
          error.response?.data
            ?.message ||
            "Unable to validate coupon."
        );
      } finally {
        setCouponLoading(false);
      }
    };

  // ==========================================================
  // REMOVE COUPON
  // ==========================================================

  const handleRemoveCoupon =
    () => {
      setCouponCode("");

      setCouponDiscount(0);

      setCouponDiscountPercentage(
        0
      );

      setAppliedCoupon(
        null
      );

      setCouponMessage("");

      setCouponError("");
    };

  // ==========================================================
  // CONFIRM BOOKING
  // ==========================================================

  const handleConfirmBooking =
    async () => {
      if (submitting) {
        return;
      }

      // ------------------------------------------------------
      // FIRST NAME
      // ------------------------------------------------------

      if (
        !customer.firstName.trim()
      ) {
        alert(
          `${pt(
            "personaldtls2",
            "First name"
          )} is required.`
        );

        return;
      }

      // ------------------------------------------------------
      // LAST NAME
      // ------------------------------------------------------

      if (
        !customer.lastName.trim()
      ) {
        alert(
          `${pt(
            "personaldtls3",
            "Family name"
          )} is required.`
        );

        return;
      }

      // ------------------------------------------------------
      // EMAIL
      // ------------------------------------------------------

      if (
        !customer.email.trim()
      ) {
        alert(
          `${pt(
            "personaldtls4",
            "Email ID"
          )} is required.`
        );

        return;
      }

      // ------------------------------------------------------
      // EMAIL FORMAT
      // ------------------------------------------------------

      const emailRegex =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (
        !emailRegex.test(
          customer.email.trim()
        )
      ) {
        alert(
          "Please enter a valid email address."
        );

        return;
      }

      // ------------------------------------------------------
      // CONFIRM EMAIL
      // ------------------------------------------------------

      if (
        !customer.confirmEmail.trim()
      ) {
        alert(
          `${pt(
            "personaldtls5",
            "Confirm Email ID"
          )} is required.`
        );

        return;
      }

      // ------------------------------------------------------
      // EMAIL MATCH
      // ------------------------------------------------------

      if (
        customer.email.trim() !==
        customer.confirmEmail.trim()
      ) {
        alert(
          "Email ID and Confirm Email ID do not match."
        );

        return;
      }

      // ------------------------------------------------------
      // PHONE
      // ------------------------------------------------------

      if (
        !customer.phone.trim() &&
        !customer.mobile.trim()
      ) {
        alert(
          pt(
            "personaldtls6",
            "Please provide at least one mobile number"
          )
        );

        return;
      }

      // ------------------------------------------------------
      // TERMS
      // ------------------------------------------------------

      if (!acceptedTerms) {
        alert(
          bt(
            "acceptTermsRequired",
            "Please accept the terms and conditions."
          )
        );

        return;
      }

      // ======================================================
      // ACTIVE FREE EXTRAS
      // ======================================================

      const activeFreeExtras =
        {};

      freeExtras.forEach(
        (item) => {
          const id =
            Number(
              item.optdet_id ??
                item.optitem_id
            );

          if (
            freeExtraChecked[id]
          ) {
            activeFreeExtras[id] =
              Number(
                selectedFreeExtras[
                  id
                ] || 1
              );
          }
        }
      );

      // ======================================================
      // ACTIVE PAID EXTRAS
      // ======================================================

      const activePaidExtras =
        {};

      paidExtras.forEach(
        (item) => {
          const id =
            Number(
              item.optdet_id ??
                item.optitem_id
            );

          if (
            paidExtraChecked[id]
          ) {
            activePaidExtras[id] =
              Number(
                selectedPaidExtras[
                  id
                ] || 1
              );
          }
        }
      );

      // ======================================================
      // ACTIVE FREE INSURANCE
      // ======================================================

      const activeFreeInsurance =
        [];

      freeInsurance.forEach(
        (item) => {
          const id =
            Number(
              item.insitem_id
            );

          if (
            freeInsuranceChecked[
              id
            ]
          ) {
            activeFreeInsurance.push(
              id
            );
          }
        }
      );

      // ======================================================
      // ACTIVE PAID INSURANCE
      // ======================================================

      const activePaidInsurance =
        [];

      paidInsurance.forEach(
        (item) => {
          const id =
            Number(
              item.insdtl_id ??
                item.insitem_id
            );

          if (
            paidInsuranceChecked[
              id
            ]
          ) {
            activePaidInsurance.push(
              id
            );
          }
        }
      );

      // ======================================================
      // BOOKING DATA
      // ======================================================

      const bookingData = {
        car,

        bookingCriteria,

        rentalDays,

        rentalPrice,

        pickupCharge,

        dropoffCharge,

        oneWayCharge,

        rentalTotal,

        freeExtras:
          activeFreeExtras,

        paidExtras:
          activePaidExtras,

        freeExtraSelection:
          selectedFreeExtras,

        paidExtraSelection:
          selectedPaidExtras,

        freeExtraChecked,

        paidExtraChecked,

        freeInsurance:
          activeFreeInsurance,

        paidInsurance:
          activePaidInsurance,

        freeInsuranceChecked,

        paidInsuranceChecked,

        paidExtrasTotal,

        paidInsuranceTotal,

        totalExtraCharges,

        // ================================================
        // COUPON
        // ================================================

        couponCode:
          appliedCoupon?.code ||
          "",

        couponDiscount:
          couponDiscountAmount,

        couponDiscountPercentage:
          couponDiscountPercentage,

        appliedCoupon,

        totalBeforeCoupon,

        finalTotal,
      };

      // ======================================================
      // CREATE BOOKING
      // ======================================================

      try {
        setSubmitting(true);

        const couponType =
          appliedCoupon?.discountType ||
          "";

        const couponValue =
          Number(
            appliedCoupon?.discountValue ||
              0
          );

        /*
         * Existing PHP convention:
         *
         * 0 = no discount
         * 1 = percentage
         * 2 = fixed amount
         */

        const discountTypeValue =
          appliedCoupon
            ? couponType ===
              "percentage"
              ? 1
              : 2
            : 0;

        // ====================================================
        // PAYLOAD
        // ====================================================

        const payload = {
          // ==================================================
          // CUSTOMER
          // ==================================================

          online_title:
            customer.title,

          online_firstname:
            customer.firstName,

          online_lastname:
            customer.lastName,

          online_telephone:
            customer.phone,

          online_mobileno:
            customer.mobile ||
            customer.phone,

          online_gender: "",

          online_emailid:
            customer.email,

          online_postcode:
            customer.postcode,

          online_city:
            customer.city,

          online_country:
            customer.country,

          online_address:
            customer.address,

          // ==================================================
          // COMMENTS
          // ==================================================

          online_remarks:
            customer.remarks,

          online_dropoffremarks:
            customer.dropoffRemarks,

          // ==================================================
          // LOCATIONS
          // ==================================================

          online_pickdetails:
            getLocationName(
              bookingCriteria?.cmbPickLocation
            ),

          online_dropdetails:
            getLocationName(
              bookingCriteria?.cmbDropLocation
            ),

          online_pickcity:
            getLocationName(
              bookingCriteria?.cmbPickLocation
            ),

          online_returncity:
            getLocationName(
              bookingCriteria?.cmbDropLocation
            ),

          online_pickstation:
            bookingCriteria?.cmbPickLocation ||
            "",

          online_returnstation:
            bookingCriteria?.cmbDropLocation ||
            "",

          // ==================================================
          // DATES
          // ==================================================

          online_pickdate:
            bookingCriteria?.pickdate ||
            "",

          online_returndate:
            bookingCriteria?.returndate ||
            "",

          online_picktime:
            getTime(
              bookingCriteria?.cmbpickHour,
              bookingCriteria?.cmbpickmin
            ),

          online_returntime:
            getTime(
              bookingCriteria?.cmbreturnHour,
              bookingCriteria?.cmbreturnmin
            ),

          online_diffdate:
            rentalDays,

          online_diffhour: 0,

          // ==================================================
          // CAR
          // ==================================================

          online_carmodelid:
            car?.selectedmodelid ||
            car?.carmodel_id ||
            car?.car_id ||
            car?.id ||
            0,

          online_carcatid:
            car?.selectedcatid ||
            car?.cat_id ||
            0,

          // ==================================================
          // PRICE
          // ==================================================

          online_carprice:
            rentalPrice,

          online_offlineprice:
            rentalPrice,

          online_onlineprice:
            finalTotal,

          // ==================================================
          // LOCATION CHARGES
          // ==================================================

          online_pickhourcharge: 0,

          online_drophourcharge: 0,

          online_locpickupcharge:
            pickupCharge,

          online_locdropoffcharge:
            dropoffCharge,

          online_picknightcharge: 0,

          online_dropnightcharge: 0,

          online_extrahrcharge: 0,

          // ==================================================
          // EXTRAS / INSURANCE
          // ==================================================

          online_totaloptextras:
            paidExtrasTotal,

          online_totalinsurance:
            paidInsuranceTotal,

          online_onewayrentalcharges:
            oneWayCharge,

          // ==================================================
          // DRIVER
          // ==================================================

          online_driverage:
            bookingCriteria?.cmbdrvage ||
            0,

          // ==================================================
          // COUPON
          // ==================================================

          coupon_code:
            appliedCoupon?.code ||
            "",

          coupon_discount:
            couponDiscountAmount,

          online_discpercent:
            couponType ===
            "percentage"
              ? couponValue
              : 0,

          online_disctype:
            discountTypeValue,

          online_discamount:
            couponDiscountAmount,

          // ==================================================
          // LANGUAGE / PAYMENT
          // ==================================================

          online_lang:
            languageId,

          payment_type:
            paymentMethod ===
            "paynow"
              ? 1
              : 0,

          booked_through: 1,
        };

        console.log(
          "================================="
        );

        console.log(
          "SENDING BOOKING"
        );

        console.log(
          "================================="
        );

        console.log(
          "COUPON:",
          {
            code:
              appliedCoupon?.code ||
              "",

            discount:
              couponDiscountAmount,

            percentage:
              couponDiscountPercentage,

            type:
              couponType,

            value:
              couponValue,
          }
        );

        console.log(
          "RENTAL PRICE:",
          rentalPrice
        );

        console.log(
          "EXTRA CHARGES:",
          totalExtraCharges
        );

        console.log(
          "TOTAL BEFORE COUPON:",
          totalBeforeCoupon
        );

        console.log(
          "COUPON DISCOUNT:",
          couponDiscountAmount
        );

        console.log(
          "FINAL TOTAL:",
          finalTotal
        );

        console.log(
          "BOOKING PAYLOAD:",
          payload
        );

        // ====================================================
        // CREATE BOOKING
        // ====================================================

        const response =
          await api.post(
            "/bookings/create",
            payload
          );

        const result =
          response.data;

        console.log(
          "CREATE BOOKING RESPONSE:",
          result
        );

        // ====================================================
        // CHECK RESPONSE
        // ====================================================

        if (
          !result ||
          !result.success
        ) {
          alert(
            result?.message ||
              bt(
                "bookingCreateFailed",
                "Unable to create booking."
              )
          );

          return;
        }

        // ====================================================
        // FINAL BOOKING
        // ====================================================

        const finalBooking = {
          bookingData,

          customer,

          paymentMethod,

          acceptedTerms,

          couponCode:
            appliedCoupon?.code ||
            "",

          couponDiscount:
            couponDiscountAmount,

          couponDiscountPercentage:
            couponDiscountPercentage,

          appliedCoupon,

          confirmedAt:
            new Date().toISOString(),

          bookingid:
            result.bookingid,

          fullbooking_no:
            result.fullbooking_no,
        };

        // ====================================================
        // SAVE FINAL BOOKING
        // ====================================================

        sessionStorage.setItem(
          "finalBooking",
          JSON.stringify(
            finalBooking
          )
        );

        sessionStorage.setItem(
          "bookingStepThree",
          JSON.stringify(
            bookingData
          )
        );

        // ====================================================
        // NAVIGATE
        // ====================================================

        navigate(
          "/confirmbooking",
          {
            state: {
              bookingid:
                result.bookingid,
            },
          }
        );
      } catch (error) {
        console.error(
          "CREATE BOOKING ERROR:",
          error
        );

        console.error(
          "Response:",
          error.response
        );

        console.error(
          "Response Data:",
          error.response?.data
        );

        alert(
          error.response?.data
            ?.message ||
            bt(
              "bookingServerError",
              "Server error while creating booking."
            )
        );
      } finally {
        setSubmitting(false);
      }
    };

  // ==========================================================
  // PREVIOUS
  // ==========================================================

  const handlePrevious = () => {
    navigate(
      "/selected-car"
    );
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div
        className="text-center"
        style={{
          padding:
            "100px 20px",
        }}
      >
        <h4>
          {bt(
            "loadingBookingDetails",
            "Loading booking details..."
          )}
        </h4>
      </div>
    );
  }

  if (!car) {
    return null;
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div className="p20">
      <div className="p0 container container2 p0">

        {/* ==================================================
            SIDEBAR
        ================================================== */}

        <div
          className="col-md-3 sidebar"
          style={{
            float: "left",
          }}
        >
          <div className="booking-wrapper">

            {/* BOOKING SUMMARY */}

            <div className="booking-card">

              <div className="card-header">

                <h3>
                  {bt(
                    "yourBooking",
                    "Your booking"
                  )}
                </h3>

                <label
                  className="search-btn"
                  onClick={() =>
                    navigate(
                      "/bookacar"
                    )
                  }
                  style={{
                    cursor:
                      "pointer",
                  }}
                >
                  {bt(
                    "modify",
                    "Modify"
                  )}{" "}
                  <i className="fa-solid fa-arrow-down"></i>
                </label>

              </div>

              {/* PICKUP */}

              <div className="booking-item">

                <div className="icon-area">

                  <div className="icon">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>

                  <div className="line"></div>

                </div>

                <div className="booking-content">

                  <h4>
                    {getLocationName(
                      bookingCriteria?.cmbPickLocation
                    )}
                  </h4>

                  <p>
                    {formatDate(
                      bookingCriteria?.pickdate
                    )}
                    {" @ "}
                    {getTime(
                      bookingCriteria?.cmbpickHour,
                      bookingCriteria?.cmbpickmin
                    )}
                  </p>

                </div>

              </div>

              {/* RETURN */}

              <div className="booking-item">

                <div className="icon-area">

                  <div className="icon">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>

                </div>

                <div className="booking-content">

                  <h4>
                    {getLocationName(
                      bookingCriteria?.cmbDropLocation
                    )}
                  </h4>

                  <p>
                    {formatDate(
                      bookingCriteria?.returndate
                    )}
                    {" @ "}
                    {getTime(
                      bookingCriteria?.cmbreturnHour,
                      bookingCriteria?.cmbreturnmin
                    )}
                  </p>

                </div>

              </div>

            </div>

            {/* YOUR CAR */}

            <div className="your-car">
              {bt(
                "yourCar",
                "Your Car"
              )}
            </div>

            <div className="car-box price-box">

              <h2
                className="car-title"
                style={{
                  textAlign:
                    "center",
                  fontSize:
                    "20px",
                  lineHeight:
                    "19px",
                }}
              >
                {carName}

                <br />

                <span
                  style={{
                    fontSize:
                      "14px",
                    fontWeight:
                      500,
                  }}
                >
                  {category ||
                    bt(
                      "orSimilar",
                      "or similar"
                    )}
                </span>
              </h2>

              {carImage && (
                <img
                  src={carImage}
                  alt={carName}
                  className="car-img"
                  onError={(e) => {
                    e.currentTarget.style.display =
                      "none";
                  }}
                />
              )}

              <div
                className="specs-row"
                style={{
                  textAlign:
                    "center",
                  margin:
                    "0 14px",
                }}
              >
                <span className="spec-tag">
                  👤 {passengers}
                </span>

                <span className="spec-tag">
                  🧳 {luggage}
                </span>

                <span className="spec-tag">
                  ❄️{" "}
                  {airCondition}
                </span>

                <span className="spec-tag">
                  ⚙️{" "}
                  {transmission}
                </span>
              </div>

              <p
                className="car-d"
                style={{
                  textAlign:
                    "center",
                }}
              >
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();

                    navigate(
                      "/selected-car"
                    );
                  }}
                >
                  {bt(
                    "carModify",
                    "Car Modify"
                  )}
                </a>
              </p>

              <p className="big-size-text1">
                {bt(
                  "carRentalFee",
                  "Car rental fee"
                )}
              </p>

              <p className="big-size-text">
                €{" "}
                {rentalPrice.toFixed(
                  2
                )}
              </p>

            </div>

          </div>

          <div className="clearfix"></div>
        </div>

        {/* ==================================================
            MAIN CONTENT
        ================================================== */}

        <div
          className="col-md-9 main-content"
          style={{
            float: "left",
          }}
        >

          {/* ==================================================
              RATE CHART
          ================================================== */}

          <div className="rate">
            {rt(
              "ratechart4",
              "Rate charts"
            )} :
          </div>

          <div
            className="price-box"
            style={{
              padding: 0,
            }}
          >
            <div className="rate-box">

              <div
                style={{
                  float:
                    "left",
                  width:
                    "100%",
                }}
              >

                <table
                  className="table word-break"
                  width="100%"
                  cellSpacing="2"
                  cellPadding="2"
                  border="0"
                  style={{
                    margin: 0,
                  }}
                >

                  <tbody>

                    <tr>

                      <td
                        width="49%"
                        className="tbltxt"
                      >
                        {rt(
                          "ratechart0",
                          "Quote"
                        )}
                      </td>

                      <td
                        width="28%"
                        align="center"
                        className="tr tbltxt"
                      >
                        {rt(
                          "ratechart1",
                          "Calculation"
                        )}
                      </td>

                      <td
                        width="23%"
                        align="right"
                        className="tr tbltxt"
                      >
                        {rt(
                          "ratechart2",
                          "Total"
                        )}
                      </td>

                    </tr>

                    <tr>

                      <td className="priceh2">
                        {rt(
                          "ratechart5",
                          "Rental price"
                        )}
                      </td>

                      <td
                        align="center"
                        className="tr priceh2"
                        style={{
                          color:
                            "#0a9311",
                        }}
                      >
                        <strong>
                          {rentalDays}{" "}
                          x{" "}
                          {pricePerDay.toFixed(
                            2
                          )} €
                        </strong>
                      </td>

                      <td
                        align="right"
                        className="tr priceh2"
                        style={{
                          color:
                            "#0a9311",
                        }}
                      >
                        <strong>
                          {rentalPrice.toFixed(
                            2
                          )} €
                        </strong>
                      </td>

                    </tr>

                    <tr>

                      <td
                        className="tbltxtnew"
                        colSpan="2"
                      >
                        {rt(
                          "ratechart6",
                          "Pick up charge"
                        )}
                      </td>

                      <td
                        className="tbltxtnew"
                        align="right"
                      >
                        {pickupCharge.toFixed(
                          2
                        )} €
                      </td>

                    </tr>

                    <tr>

                      <td
                        className="tbltxtnew"
                        colSpan="2"
                      >
                        {rt(
                          "ratechart3",
                          "Drop off charge"
                        )}
                      </td>

                      <td
                        className="tbltxtnew"
                        align="right"
                      >
                        {dropoffCharge.toFixed(
                          2
                        )} €
                      </td>

                    </tr>

                    <tr>

                      <td
                        className="tbltxtnew"
                        colSpan="2"
                      >
                        {rt(
                          "ratechart19",
                          "One way rental charges"
                        )}
                      </td>

                      <td
                        className="tbltxtnew"
                        align="right"
                      >
                        {oneWayCharge.toFixed(
                          2
                        )} €
                      </td>

                    </tr>

                    <tr>

                      <td
                        colSpan="2"
                        className="priceh2"
                      >
                        <strong>
                          {rt(
                            "ratechart10",
                            "Total rental price"
                          )}
                        </strong>
                      </td>

                      <td
                        className="priceh2"
                        align="right"
                        style={{
                          color:
                            "#8a74dd",
                        }}
                      >
                        <strong>
                          {rentalTotal.toFixed(
                            2
                          )} €
                        </strong>
                      </td>

                    </tr>

                    <tr>

                      <td
                        colSpan="2"
                        className="priceh2"
                      >
                        <strong>
                          {rt(
                            "ratechart13",
                            "Total extra charges"
                          )}
                        </strong>
                      </td>

                      <td
                        align="right"
                        style={{
                          color:
                            "indianred",
                        }}
                      >
                        <strong>
                          {totalExtraCharges.toFixed(
                            2
                          )} €
                        </strong>
                      </td>

                    </tr>

                    {/* COUPON DISCOUNT */}

                    {couponDiscountAmount >
                      0 && (
                      <tr>

                        <td
                          colSpan="2"
                          className="priceh2"
                          style={{
                            color:
                              "#0a9311",
                          }}
                        >
                          <strong>
                            Coupon Discount
                          </strong>

                          {appliedCoupon?.code && (
                            <>
                              {" "}
                              (
                              {
                                appliedCoupon.code
                              }
                              )
                            </>
                          )}

                          {couponDiscountPercentage >
                            0 && (
                            <span
                              style={{
                                fontSize:
                                  "13px",
                                marginLeft:
                                  "8px",
                              }}
                            >
                              (
                              {
                                couponDiscountPercentage
                              }
                              %)
                            </span>
                          )}
                        </td>

                        <td
                          align="right"
                          className="priceh2"
                          style={{
                            color:
                              "#0a9311",
                          }}
                        >
                          <strong>
                            - €{" "}
                            {couponDiscountAmount.toFixed(
                              2
                            )}
                          </strong>
                        </td>

                      </tr>
                    )}

                    {/* FINAL TOTAL */}

                    <tr
                      style={{
                        background:
                          "#edf7ec",
                      }}
                    >

                      <td
                        colSpan="2"
                        className="pricehl"
                      >
                        <strong>
                          {rt(
                            "ratechart14",
                            "Final total rental price"
                          )}
                        </strong>
                      </td>

                      <td align="right">

                        <strong className="estimatedtotalprice">
                          {finalTotal.toFixed(
                            2
                          )}
                        </strong>{" "}
                        €
                      </td>

                    </tr>

                  </tbody>

                </table>

              </div>

            </div>

            <div className="clearfix"></div>
          </div>

          {/* ==================================================
              FREE OPTIONAL EXTRAS
          ================================================== */}

          <div className="extra-details">
            {et(
              "extracharges0",
              "Extra details"
            )} :
          </div>

          <div className="optiona-extras">
            {et(
              "extracharges1",
              "Free optional extras"
            )}
          </div>

          <div
            className="car-box price-box"
            style={{
              padding: 0,
            }}
          >

            <table
              className="table word-break"
              width="100%"
              border="0"
              cellSpacing="2"
              cellPadding="2"
            >

              <thead>

                <tr>

                  <td
                    className="optionalextra"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    {et(
                      "extracharges4",
                      "Items"
                    )}
                  </td>

                  <td
                    width="80"
                    align="center"
                    className="optionalextra"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    {et(
                      "extracharges2",
                      "Selection"
                    )}
                  </td>

                  <td
                    width="20"
                    align="center"
                    className="extrasinftxt"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    &nbsp;
                  </td>

                </tr>

              </thead>

              <tbody>

                {freeExtras.map(
                  (item) => {
                    const id =
                      Number(
                        item.optdet_id ??
                          item.optitem_id
                      );

                    const quantity =
                      Number(
                        selectedFreeExtras[
                          id
                        ] || 1
                      );

                    const checked =
                      freeExtraChecked[
                        id
                      ] === true;

                    const itemName =
                      item.name ||
                      item.optitem_name ||
                      item.optdet_name ||
                      "";

                    return (
                      <tr
                        key={id}
                        style={{
                          opacity:
                            checked
                              ? 1
                              : 0.65,
                        }}
                      >

                        <td
                          className="bookingtbltxt"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >
                          {itemName}
                        </td>

                        <td
                          align="center"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >
                          <select
                            className="verysmallfrmtxtbox"
                            value={
                              quantity
                            }
                            disabled={
                              !checked
                            }
                            onChange={(e) =>
                              updateFreeExtra(
                                id,
                                e.target.value
                              )
                            }
                          >
                            {Array.from(
                              {
                                length:
                                  Math.max(
                                    1,
                                    Number(
                                      item.max ||
                                        1
                                    )
                                  ),
                              },
                              (
                                _,
                                index
                              ) =>
                                index +
                                1
                            ).map(
                              (
                                number
                              ) => (
                                <option
                                  key={
                                    number
                                  }
                                  value={
                                    number
                                  }
                                >
                                  {
                                    number
                                  }
                                </option>
                              )
                            )}
                          </select>
                        </td>

                        <td
                          align="center"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              checked
                            }
                            onChange={() =>
                              toggleFreeExtra(
                                id
                              )
                            }
                            style={{
                              cursor:
                                "pointer",
                              width:
                                "16px",
                              height:
                                "16px",
                            }}
                          />
                        </td>

                      </tr>
                    );
                  }
                )}

              </tbody>

            </table>

          </div>

          <div className="smallmask"></div>

          {/* ==================================================
              PAID OPTIONAL EXTRAS
          ================================================== */}

          <div className="optiona-extras">
            {et(
              "extracharges3",
              "Optional Extras"
            )}
          </div>

          <div
            className="car-box price-box"
            style={{
              padding: 0,
            }}
          >

            <div className="optionalcon">

              <div className="extras">

                <table
                  width="100%"
                  border="0"
                  cellSpacing="2"
                  cellPadding="2"
                  className="table word-break"
                >

                  <thead>

                    <tr>

                      <td
                        className="optionalextra"
                        width="43%"
                        style={{
                          background:
                            "#c7e7ef",
                        }}
                      >
                        {et(
                          "extracharges4",
                          "Items"
                        )}
                      </td>

                      <td
                        className="optionalextra"
                        width="20%"
                        style={{
                          background:
                            "#c7e7ef",
                        }}
                      >
                        {et(
                          "extracharges5",
                          "Rate per day"
                        )}
                      </td>

                      <td
                        className="optionalextra"
                        width="17%"
                        style={{
                          background:
                            "#c7e7ef",
                        }}
                      >
                        {et(
                          "extracharges6",
                          "Total"
                        )}
                      </td>

                      <td
                        className="optionalextra"
                        width="13%"
                        align="center"
                        style={{
                          background:
                            "#c7e7ef",
                        }}
                      >
                        {et(
                          "extracharges2",
                          "Selection"
                        )}
                      </td>

                      <td
                        className="optionalextra"
                        width="7%"
                        align="center"
                        style={{
                          background:
                            "#c7e7ef",
                        }}
                      >
                        &nbsp;
                      </td>

                    </tr>

                  </thead>

                  <tbody>

                    {paidExtras.map(
                      (item) => {
                        const id =
                          Number(
                            item.optdet_id ??
                              item.optitem_id
                          );

                        const quantity =
                          Number(
                            selectedPaidExtras[
                              id
                            ] || 1
                          );

                        const isChecked =
                          paidExtraChecked[
                            id
                          ] === true;

                        const itemName =
                          item.name ||
                          item.optitem_name ||
                          item.optdet_name ||
                          "";

                        const description =
                          item.description ||
                          item.optitem_description ||
                          item.optdet_description ||
                          "";

                        const dayRate =
                          Number(
                            item.dayRate ??
                              item.day_rate ??
                              item.ratePerDay ??
                              item.rate_per_day ??
                              0
                          );

                        const baseTotal =
                          getPaidExtraBaseTotal(
                            item
                          );

                        const itemTotal =
                          baseTotal *
                          quantity;

                        return (
                          <tr
                            key={id}
                            style={{
                              opacity:
                                isChecked
                                  ? 1
                                  : 0.65,
                            }}
                          >

                            <td
                              className="bookingtbltxt"
                              style={{
                                background:
                                  "#F0F0F0",
                              }}
                            >

                              <strong>
                                {itemName}
                              </strong>

                              {description &&
                                description.trim() !==
                                  itemName.trim() && (
                                  <div
                                    style={{
                                      fontSize:
                                        "13px",
                                      color:
                                        "#666",
                                      marginTop:
                                        "3px",
                                    }}
                                  >
                                    {
                                      description
                                    }
                                  </div>
                                )}

                            </td>

                            <td
                              className="bookingtbltxt"
                              style={{
                                background:
                                  "#F0F0F0",
                              }}
                            >
                              <span className="item-name">
                                {et(
                                  "extracharges5",
                                  "Rate per day"
                                )}{" "}
                                :{" "}
                              </span>

                              {dayRate.toFixed(
                                2
                              )}{" "}
                              €/
                              {et(
                                "extracharges10",
                                "Day"
                              )}
                            </td>

                            <td
                              className="bookingtbltxt"
                              style={{
                                background:
                                  "#F0F0F0",
                              }}
                            >

                              <span className="item-name">
                                {et(
                                  "extracharges6",
                                  "Total"
                                )}{" "}
                                :{" "}
                              </span>

                              <div>

                                <strong
                                  style={{
                                    color:
                                      isChecked
                                        ? "#8a74dd"
                                        : "#999",
                                  }}
                                >
                                  {itemTotal.toFixed(
                                    2
                                  )}{" "}
                                  €
                                </strong>

                              </div>

                            </td>

                            <td
                              className="tc"
                              align="center"
                              style={{
                                background:
                                  "#F0F0F0",
                              }}
                            >

                              <span className="item-name">
                                {et(
                                  "extracharges2",
                                  "Selection"
                                )}{" "}
                                :{" "}
                              </span>

                              <select
                                className="verysmallfrmtxtbox"
                                value={
                                  quantity
                                }
                                disabled={
                                  !isChecked
                                }
                                onChange={(e) =>
                                  updatePaidExtra(
                                    id,
                                    e.target.value
                                  )
                                }
                              >

                                {Array.from(
                                  {
                                    length:
                                      Math.max(
                                        1,
                                        Number(
                                          item.max ||
                                            1
                                        )
                                      ),
                                  },
                                  (
                                    _,
                                    index
                                  ) =>
                                    index +
                                    1
                                ).map(
                                  (
                                    number
                                  ) => (
                                    <option
                                      key={
                                        number
                                      }
                                      value={
                                        number
                                      }
                                    >
                                      {
                                        number
                                      }
                                    </option>
                                  )
                                )}

                              </select>

                            </td>

                            <td
                              className="tc"
                              align="center"
                              style={{
                                background:
                                  "#F0F0F0",
                              }}
                            >

                              <input
                                type="checkbox"
                                checked={
                                  isChecked
                                }
                                onChange={() =>
                                  togglePaidExtra(
                                    id
                                  )
                                }
                                style={{
                                  cursor:
                                    "pointer",
                                  width:
                                    "16px",
                                  height:
                                    "16px",
                                }}
                              />

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>

            </div>

          </div>

          <div className="smallmask"></div>

          {/* ==================================================
              FREE INSURANCE
          ================================================== */}

          <div className="insurances">
            {et(
              "extracharges9",
              "Free insurance"
            )}
          </div>

          <div className="insurances-bg price-box">

            <table
              width="100%"
              border="0"
              cellSpacing="2"
              cellPadding="2"
              className="table word-break"
            >

              <thead>

                <tr>

                  <td
                    className="optionalextra"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    {et(
                      "extracharges4",
                      "Items"
                    )}
                  </td>

                  <td
                    width="20"
                    align="center"
                    className="extrasinftxt"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    {et(
                      "extracharges2",
                      "Selection"
                    )}
                  </td>

                </tr>

              </thead>

              <tbody>

                {freeInsurance.map(
                  (insurance) => {
                    const id =
                      Number(
                        insurance.insitem_id
                      );

                    const checked =
                      freeInsuranceChecked[
                        id
                      ] === true;

                    const insuranceName =
                      insurance.name ||
                      "";

                    return (
                      <tr
                        key={id}
                        style={{
                          opacity:
                            checked
                              ? 1
                              : 0.65,
                        }}
                      >

                        <td
                          className="bookingtbltxt"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >

                          <img
                            src="/images/insurances-icon.png"
                            alt=""
                            style={{
                              marginRight:
                                "8px",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display =
                                "none";
                            }}
                          />

                          {insuranceName}

                        </td>

                        <td
                          align="center"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >

                          <input
                            type="checkbox"
                            checked={
                              checked
                            }
                            onChange={() =>
                              toggleFreeInsurance(
                                id
                              )
                            }
                            style={{
                              cursor:
                                "pointer",
                              width:
                                "16px",
                              height:
                                "16px",
                            }}
                          />

                        </td>

                      </tr>
                    );
                  }
                )}

              </tbody>

            </table>

          </div>

          <div className="smallmask"></div>

          {/* ==================================================
              PAID INSURANCE
          ================================================== */}

          <div className="insurances">
            {et(
              "extracharges7",
              "Insurances"
            )}
          </div>

          <div className="insurances-bg price-box">

            <table
              width="100%"
              border="0"
              cellSpacing="2"
              cellPadding="2"
              className="table word-break"
            >

              <thead>

                <tr>

                  <td
                    className="optionalextra"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    {et(
                      "extracharges4",
                      "Items"
                    )}
                  </td>

                  <td
                    className="optionalextra"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    {et(
                      "extracharges5",
                      "Rate per day"
                    )}
                  </td>

                  <td
                    className="optionalextra"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    {et(
                      "extracharges6",
                      "Total"
                    )}
                  </td>

                  <td
                    width="20"
                    align="center"
                    className="extrasinftxt"
                    style={{
                      background:
                        "#c7e7ef",
                    }}
                  >
                    &nbsp;
                  </td>

                </tr>

              </thead>

              <tbody>

                {paidInsurance.map(
                  (insurance) => {
                    const id =
                      Number(
                        insurance.insdtl_id ??
                          insurance.insitem_id
                      );

                    const checked =
                      paidInsuranceChecked[
                        id
                      ] === true;

                    const insuranceName =
                      insurance.name ||
                      "";

                    const ratePerDay =
                      Number(
                        insurance.chargePerDay ??
                          insurance.charge_per_day ??
                          0
                      );

                    const total =
                      ratePerDay *
                      rentalDays;

                    return (
                      <tr
                        key={id}
                        style={{
                          opacity:
                            checked
                              ? 1
                              : 0.65,
                        }}
                      >

                        <td
                          className="bookingtbltxt"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >

                          <img
                            src="/images/insurances-icon.png"
                            alt=""
                            style={{
                              marginRight:
                                "8px",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display =
                                "none";
                            }}
                          />

                          {insuranceName}

                        </td>

                        <td
                          className="bookingtbltxt"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >
                          {ratePerDay.toFixed(
                            2
                          )}{" "}
                          €/
                          {et(
                            "extracharges10",
                            "Day"
                          )}
                        </td>

                        <td
                          className="bookingtbltxt"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >

                          <strong>
                            {total.toFixed(
                              2
                            )}{" "}
                            €
                          </strong>

                        </td>

                        <td
                          align="center"
                          style={{
                            background:
                              "#F0F0F0",
                          }}
                        >

                          <input
                            type="checkbox"
                            checked={
                              checked
                            }
                            onChange={() =>
                              togglePaidInsurance(
                                id
                              )
                            }
                            style={{
                              cursor:
                                "pointer",
                              width:
                                "16px",
                              height:
                                "16px",
                            }}
                          />

                        </td>

                      </tr>
                    );
                  }
                )}

              </tbody>

            </table>

          </div>

          <div className="smallmask"></div>

          {/* ==================================================
              PERSONAL DETAILS
          ================================================== */}

          <div className="rate">
            {pt(
              "personaldtls0",
              "Personal details"
            )}
          </div>

          <div
            className="car-box price-box"
            style={{
              padding:
                "24px",
              marginBottom:
                "18px",
            }}
          >

            <div className="row">

              {/* TITLE */}

              <div className="col-md-3 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls1",
                    "Title"
                  )}
                </label>

                <select
                  className="form-control"
                  name="title"
                  value={
                    customer.title
                  }
                  onChange={
                    handleCustomerChange
                  }
                >

                  <option value="">
                    {bt(
                      "select",
                      "Select"
                    )}
                  </option>

                  <option value="Mr">
                    {pt(
                      "personaldtls25",
                      "Mr."
                    )}
                  </option>

                  <option value="Mrs">
                    {pt(
                      "personaldtls26",
                      "Mrs."
                    )}
                  </option>

                  <option value="Ms">
                    {pt(
                      "personaldtls27",
                      "Ms."
                    )}
                  </option>

                  <option value="Dr">
                    {pt(
                      "personaldtls28",
                      "Dr."
                    )}
                  </option>

                </select>

              </div>

              {/* FIRST NAME */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls2",
                    "First name"
                  )}{" "}
                  *
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="firstName"
                  value={
                    customer.firstName
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls2",
                    "First name"
                  )}
                />

              </div>

              {/* LAST NAME */}

              <div className="col-md-5 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls3",
                    "Family name"
                  )}{" "}
                  *
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="lastName"
                  value={
                    customer.lastName
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls3",
                    "Family name"
                  )}
                />

              </div>

              {/* EMAIL */}

              <div className="col-md-6 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls4",
                    "Email ID"
                  )}{" "}
                  *
                </label>

                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={
                    customer.email
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls4",
                    "Email ID"
                  )}
                />

              </div>

              {/* CONFIRM EMAIL */}

              <div className="col-md-6 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls5",
                    "Confirm Email ID"
                  )}{" "}
                  *
                </label>

                <input
                  type="email"
                  className="form-control"
                  name="confirmEmail"
                  value={
                    customer.confirmEmail
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls5",
                    "Confirm Email ID"
                  )}
                />

              </div>

              {/* PHONE */}

              <div className="col-md-6 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls7",
                    "Phone no"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="phone"
                  value={
                    customer.phone
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls7",
                    "Phone no"
                  )}
                />

              </div>

              {/* MOBILE */}

              <div className="col-md-6 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls8",
                    "Mobile no"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="mobile"
                  value={
                    customer.mobile
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls8",
                    "Mobile no"
                  )}
                />

              </div>

              {/* ADDRESS */}

              <div className="col-md-12 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls14",
                    "Home Address"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="address"
                  value={
                    customer.address
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls14",
                    "Home Address"
                  )}
                />

              </div>

              {/* CITY */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls15",
                    "City"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="city"
                  value={
                    customer.city
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls15",
                    "City"
                  )}
                />

              </div>

              {/* COUNTRY */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls10",
                    "Country"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="country"
                  value={
                    customer.country
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls10",
                    "Country"
                  )}
                />

              </div>

              {/* POSTCODE */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls16",
                    "Zip code"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="postcode"
                  value={
                    customer.postcode
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls16",
                    "Zip code"
                  )}
                />

              </div>

              {/* PICKUP */}

              <div className="col-md-6 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls9",
                    "Pick up location"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  value={getLocationName(
                    bookingCriteria?.cmbPickLocation
                  )}
                  readOnly
                />

              </div>

              {/* DROP */}

              <div className="col-md-6 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls17",
                    "Drop off location"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  value={getLocationName(
                    bookingCriteria?.cmbDropLocation
                  )}
                  readOnly
                />

              </div>

              {/* DRIVER AGE */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls18",
                    "Age driver"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  value={
                    bookingCriteria?.cmbdrvage ||
                    ""
                  }
                  readOnly
                />

              </div>

              {/* INFORMATION */}

              <div className="col-md-12 mb-3">

                <div
                  style={{
                    background:
                      "#f7f7f7",
                    padding:
                      "12px",
                    marginBottom:
                      "12px",
                    fontSize:
                      "14px",
                  }}
                >

                  <strong>
                    {pt(
                      "personaldtls19",
                      "Visitor must enter following informations in the Pick up and Drop off comments Field"
                    )}
                  </strong>

                  <br />

                  <span>
                    {pt(
                      "personaldtls20",
                      "If Airport: Pick Up : Flight number + arrival time of your flight; Drop off : departure time of your flight"
                    )}
                  </span>

                  <br />

                  <span>
                    {pt(
                      "personaldtls21",
                      "If Harbor : Boat name + company + Arrival time of your boat"
                    )}
                  </span>

                  <br />

                  <span>
                    {pt(
                      "personaldtls22",
                      "If accommodation : Name, town, telephone number (if known)"
                    )}
                  </span>

                </div>

                <label className="form-label">
                  {pt(
                    "personaldtls23",
                    "Pick up comments"
                  )}
                </label>

                <textarea
                  className="form-control"
                  rows="3"
                  name="remarks"
                  value={
                    customer.remarks
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls23",
                    "Pick up comments"
                  )}
                />

              </div>

              {/* DROP COMMENTS */}

              <div className="col-md-12 mb-3">

                <label className="form-label">
                  {pt(
                    "personaldtls24",
                    "Drop off comments"
                  )}
                </label>

                <textarea
                  className="form-control"
                  rows="3"
                  name="dropoffRemarks"
                  value={
                    customer.dropoffRemarks
                  }
                  onChange={
                    handleCustomerChange
                  }
                  placeholder={pt(
                    "personaldtls24",
                    "Drop off comments"
                  )}
                />

              </div>

            </div>

          </div>

          {/* ==================================================
              PAYMENT
          ================================================== */}

          <div
            className="car-box price-box"
            style={{
              padding:
                "24px",
              marginBottom:
                "18px",
            }}
          >

            <h4
              style={{
                marginBottom:
                  "14px",
                fontSize:
                  "16px",
                fontWeight:
                  600,
              }}
            >
              {bt(
                "selectPaymentMethod",
                "Select Payment Method"
              )}
            </h4>

            <div
              style={{
                display:
                  "flex",
                gap:
                  "24px",
                flexWrap:
                  "wrap",
              }}
            >

              <label
                style={{
                  display:
                    "flex",
                  alignItems:
                    "center",
                  cursor:
                    "pointer",
                }}
              >

                <input
                  type="radio"
                  name="paymentMethod"
                  value="paynow"
                  checked={
                    paymentMethod ===
                    "paynow"
                  }
                  onChange={(e) =>
                    setPaymentMethod(
                      e.target.value
                    )
                  }
                  style={{
                    marginRight:
                      "8px",
                  }}
                />

                {bt(
                  "payNow",
                  "Pay now"
                )}

              </label>

              <label
                style={{
                  display:
                    "flex",
                  alignItems:
                    "center",
                  cursor:
                    "pointer",
                }}
              >

                <input
                  type="radio"
                  name="paymentMethod"
                  value="paylater"
                  checked={
                    paymentMethod ===
                    "paylater"
                  }
                  onChange={(e) =>
                    setPaymentMethod(
                      e.target.value
                    )
                  }
                  style={{
                    marginRight:
                      "8px",
                  }}
                />

                {bt(
                  "payLater",
                  "Pay later"
                )}

              </label>

            </div>

          </div>

          {/* ==================================================
              TERMS
          ================================================== */}

          <div
            className="car-box price-box"
            style={{
              padding:
                "20px 24px",
              marginBottom:
                "18px",
            }}
          >

            <div
              style={{
                display:
                  "flex",
                alignItems:
                  "flex-start",
                gap:
                  "10px",
              }}
            >

              <input
                type="checkbox"
                id="checkConfirm"
                name="checkConfirm"
                checked={
                  acceptedTerms
                }
                onChange={(e) =>
                  setAcceptedTerms(
                    e.target.checked
                  )
                }
                style={{
                  marginTop:
                    "4px",
                  width:
                    "16px",
                  height:
                    "16px",
                  cursor:
                    "pointer",
                  flexShrink:
                    0,
                }}
              />

              <div
                style={{
                  lineHeight:
                    "1.5",
                }}
              >

                <span>
                  {pt(
                    "personaldtls12",
                    "I accept the"
                  )}{" "}
                </span>

                <a
                  href="#terms"
                  onClick={
                    openTerms
                  }
                  style={{
                    color:
                      "#337ab7",
                    textDecoration:
                      "underline",
                    cursor:
                      "pointer",
                  }}
                >
                  {termsContent?.cont_title ||
                    bt(
                      "termsConditions",
                      "Terms & Conditions"
                    )}
                </a>

              </div>

            </div>

            {termsLoading && (
              <div
                style={{
                  marginTop:
                    "8px",
                  marginLeft:
                    "26px",
                  fontSize:
                    "13px",
                  color:
                    "#777",
                }}
              >
                {bt(
                  "loading",
                  "Loading..."
                )}
              </div>
            )}

          </div>

          {/* ==================================================
              TERMS MODAL
          ================================================== */}

          {showTermsModal && (
            <div
              style={{
                position:
                  "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "rgba(0,0,0,0.55)",
                zIndex:
                  99999,
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                padding:
                  "20px",
              }}
              onClick={
                closeTerms
              }
            >

              <div
                style={{
                  width:
                    "970px",
                  maxWidth:
                    "100%",
                  height:
                    "500px",
                  maxHeight:
                    "90vh",
                  background:
                    "#fff",
                  position:
                    "relative",
                  borderRadius:
                    "4px",
                  boxShadow:
                    "0 5px 25px rgba(0,0,0,0.3)",
                  overflow:
                    "hidden",
                }}
                onClick={(e) =>
                  e.stopPropagation()
                }
              >

                <div
                  style={{
                    height:
                      "55px",
                    background:
                      "#f5f5f5",
                    borderBottom:
                      "1px solid #ddd",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "space-between",
                    padding:
                      "0 18px",
                  }}
                >

                  <h4
                    style={{
                      margin: 0,
                      fontSize:
                        "18px",
                      fontWeight:
                        600,
                    }}
                  >
                    {termsContent?.cont_title ||
                      bt(
                        "termsConditions",
                        "Terms & Conditions"
                      )}
                  </h4>

                  <button
                    type="button"
                    onClick={
                      closeTerms
                    }
                    style={{
                      border:
                        "none",
                      background:
                        "transparent",
                      fontSize:
                        "28px",
                      lineHeight:
                        "1",
                      cursor:
                        "pointer",
                      color:
                        "#555",
                    }}
                  >
                    ×
                  </button>

                </div>

                <div
                  style={{
                    height:
                      "calc(100% - 55px)",
                    overflowY:
                      "auto",
                    padding:
                      "20px",
                    fontSize:
                      "14px",
                    lineHeight:
                      "1.6",
                  }}
                >

                  {termsLoading ? (
                    <div
                      style={{
                        textAlign:
                          "center",
                        padding:
                          "50px 20px",
                      }}
                    >
                      {bt(
                        "loading",
                        "Loading..."
                      )}
                    </div>
                  ) : termsContent ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          termsContent.cont_details ||
                          "",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        textAlign:
                          "center",
                        padding:
                          "50px 20px",
                        color:
                          "#777",
                      }}
                    >
                      {bt(
                        "termsNotAvailable",
                        "Terms & Conditions are not available."
                      )}
                    </div>
                  )}

                </div>

              </div>

            </div>
          )}

          {/* ==================================================
              COUPON CODE
          ================================================== */}

          <div
            className="car-box price-box"
            style={{
              padding:
                "20px 24px",
              marginBottom:
                "18px",
            }}
          >

            <label
              className="form-label"
              style={{
                fontWeight:
                  600,
                display:
                  "block",
                marginBottom:
                  "8px",
              }}
            >
              Coupon Code
            </label>

            <div
              style={{
                display:
                  "flex",
                gap:
                  "10px",
                maxWidth:
                  "600px",
              }}
            >

              <input
                type="text"
                className="form-control"
                value={
                  couponCode
                }
                onChange={(e) => {
                  const newCode =
                    e.target.value.toUpperCase();

                  setCouponCode(
                    newCode
                  );

                  /*
                   * If coupon changes,
                   * remove old discount.
                   */

                  if (
                    appliedCoupon
                  ) {
                    setCouponDiscount(
                      0
                    );

                    setCouponDiscountPercentage(
                      0
                    );

                    setAppliedCoupon(
                      null
                    );
                  }

                  setCouponError(
                    ""
                  );

                  setCouponMessage(
                    ""
                  );
                }}
                placeholder="Enter coupon code"
                disabled={
                  couponLoading
                }
                style={{
                  height:
                    "42px",
                }}
              />

              {!appliedCoupon ? (
                <button
                  type="button"
                  className="nextbutton"
                  onClick={
                    handleApplyCoupon
                  }
                  disabled={
                    couponLoading ||
                    !couponCode.trim()
                  }
                  style={{
                    minWidth:
                      "100px",
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  {couponLoading
                    ? "Applying..."
                    : "Apply"}
                </button>
              ) : (
                <button
                  type="button"
                  className="nextbutton"
                  onClick={
                    handleRemoveCoupon
                  }
                  disabled={
                    couponLoading
                  }
                  style={{
                    minWidth:
                      "100px",
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Remove
                </button>
              )}

            </div>

            {/* SUCCESS */}

            {couponMessage && (
              <div
                style={{
                  marginTop:
                    "10px",
                  color:
                    "#0a9311",
                  fontSize:
                    "14px",
                  fontWeight:
                    500,
                }}
              >
                ✓{" "}
                {couponMessage}
              </div>
            )}

            {/* ERROR */}

            {couponError && (
              <div
                style={{
                  marginTop:
                    "10px",
                  color:
                    "#dc3545",
                  fontSize:
                    "14px",
                  fontWeight:
                    500,
                }}
              >
                {couponError}
              </div>
            )}

            {/* APPLIED COUPON */}

            {appliedCoupon && (
              <div
                style={{
                  marginTop:
                    "10px",
                  fontSize:
                    "14px",
                  color:
                    "#555",
                }}
              >

                Applied coupon:{" "}

                <strong>
                  {
                    appliedCoupon.code
                  }
                </strong>

                {" — "}

                Discount:{" "}

                <strong>
                  {couponDiscountPercentage}
                  %
                </strong>

                {" — "}

                <strong>
                  €{" "}
                  {couponDiscountAmount.toFixed(
                    2
                  )}
                </strong>

              </div>
            )}

          </div>

          {/* ==================================================
              FINAL TOTAL
          ================================================== */}

          <div
            className="price-box"
            style={{
              padding:
                "18px 24px",
              marginBottom:
                "20px",
              textAlign:
                "right",
              background:
                "#edf7ec",
            }}
          >

            <span
              style={{
                fontSize:
                  "16px",
                fontWeight:
                  600,
              }}
            >
              {rt(
                "ratechart14",
                "Final total rental price"
              )}
            </span>

            {" "}

            <strong
              className="estimatedtotalprice"
              style={{
                fontSize:
                  "22px",
              }}
            >
              {finalTotal.toFixed(
                2
              )}{" "}
              €
            </strong>

          </div>

          {/* ==================================================
              BUTTONS
          ================================================== */}

          <div
            style={{
              paddingRight:
                "2px",
              width:
                "auto",
              height:
                "auto",
              float:
                "right",
              marginRight:
                "1px",
              marginBottom:
                "30px",
            }}
          >

            <button
              type="button"
              className="nextbutton rightbut"
              onClick={
                handlePrevious
              }
              disabled={
                submitting
              }
            >
              {bt(
                "goPrevious",
                "Go Previous"
              )}
            </button>

            <button
              type="button"
              className="nextbutton1 nextbutton"
              onClick={
                handleConfirmBooking
              }
              disabled={
                submitting
              }
            >
              {submitting
                ? bt(
                    "processing",
                    "Processing..."
                  )
                : `${bt(
                    "confirmBooking",
                    "Confirm Booking"
                  )} »`}
            </button>

          </div>

          <div className="clearfix"></div>

        </div>

        <div className="clearfix"></div>

      </div>
    </div>
  );
}

export default BookACarThree;